import {
	type PublicVaultSortKey,
} from "@/hooks/usePublicProtocol";
import {
	rowBgForBand,
	type VaultRiskBand,
} from "@/lib/vault-risk";
import { cn } from "@/lib/utils";

export type BandFilter = "all" | VaultRiskBand;

export { rowBgForBand };

const STATUS_OPTIONS: { id: BandFilter; label: string }[] = [
	{ id: "all", label: "All statuses" },
	{ id: "underwater", label: "Underwater" },
	{ id: "liquidatable", label: "Liquidatable" },
	{ id: "at_risk", label: "At risk" },
	{ id: "caution", label: "Caution" },
	{ id: "healthy", label: "Healthy" },
	{ id: "empty", label: "Empty" },
];

const SORT_OPTIONS: { key: PublicVaultSortKey; label: string }[] = [
	{ key: "healthAsc", label: "Riskiest first" },
	{ key: "ltvDesc", label: "Highest LTV" },
	{ key: "debtDesc", label: "Highest debt" },
	{ key: "newest", label: "Newest" },
];

/** Collapsed dropdown filters for vault tables (owned + public). */
export function VaultFiltersBar({
	band,
	onBandChange,
	sort,
	onSortChange,
	showSort = true,
	showEmptyToggle = false,
	showEmpty = false,
	onShowEmptyChange,
	className,
}: {
	band: BandFilter;
	onBandChange: (b: BandFilter) => void;
	sort?: PublicVaultSortKey;
	onSortChange?: (s: PublicVaultSortKey) => void;
	showSort?: boolean;
	showEmptyToggle?: boolean;
	showEmpty?: boolean;
	onShowEmptyChange?: (v: boolean) => void;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex flex-wrap items-end gap-3 rounded-xl border border-border/60 bg-card/40 p-3",
				className,
			)}
		>
			<label className="flex min-w-[10rem] flex-1 flex-col gap-1">
				<span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
					Status
				</span>
				<select
					value={band}
					onChange={(e) => onBandChange(e.target.value as BandFilter)}
					className="h-10 rounded-xl border border-border bg-background px-3 text-sm font-semibold outline-none focus:border-primary"
				>
					{STATUS_OPTIONS.map((o) => (
						<option key={o.id} value={o.id}>
							{o.label}
						</option>
					))}
				</select>
			</label>

			{showSort && sort != null && onSortChange ? (
				<label className="flex min-w-[10rem] flex-1 flex-col gap-1">
					<span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
						Sort
					</span>
					<select
						value={sort}
						onChange={(e) =>
							onSortChange(e.target.value as PublicVaultSortKey)
						}
						className="h-10 rounded-xl border border-border bg-background px-3 text-sm font-semibold outline-none focus:border-primary"
					>
						{SORT_OPTIONS.map((o) => (
							<option key={o.key} value={o.key}>
								{o.label}
							</option>
						))}
					</select>
				</label>
			) : null}

			{showEmptyToggle && onShowEmptyChange ? (
				<label className="flex items-center gap-2 pb-2 text-xs font-semibold text-muted-foreground">
					<input
						type="checkbox"
						checked={showEmpty}
						onChange={(e) => onShowEmptyChange(e.target.checked)}
						className="size-4 rounded border-border"
					/>
					Show empty vaults
				</label>
			) : null}
		</div>
	);
}

/** Sort vaults: underwater → liquidatable → at_risk → caution → healthy → empty. */
export function riskBandOrder(band: VaultRiskBand | string): number {
	switch (band) {
		case "underwater":
			return 0;
		case "liquidatable":
			return 1;
		case "at_risk":
			return 2;
		case "caution":
			return 3;
		case "healthy":
			return 4;
		case "empty":
			return 5;
		default:
			return 6;
	}
}
